import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Shift } from '@wilson/interfaces';
import { MoveShiftHelperService } from '@wilson/non-domain-specific/shift-helpers/core';
import { TimelineStaysSuggestionService } from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom, forkJoin } from 'rxjs';
import { DeleteShiftCommonOperationsService } from '../../services/delete-shift-common-operations.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { UpdateTimelineAfterMoveService } from './move-multi-select-shifts-button/update-timeline-after-move.service';

@Injectable()
export class TimelineMultiSelectHelperService {
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly deleteShiftCommonOperationsService = inject(
    DeleteShiftCommonOperationsService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly moveShiftHelperService = inject(MoveShiftHelperService);
  private readonly updateTimelineAfterMoveService = inject(
    UpdateTimelineAfterMoveService,
  );

  async handleMultiShiftCellViewMoveOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      const result =
        await this.moveShiftHelperService.openDurationSelectionModal(shifts);
      if (result) {
        this.updateTimelineAfterMoveService.updateTimelineCellViewMode(result);
      }
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async handleMultiShiftStandardViewMoveOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      const result =
        await this.moveShiftHelperService.openDurationSelectionModal(shifts);
      if (result) {
        this.updateTimelineAfterMoveService.updateTimelineStandardViewMode(
          result,
        );
        this.refreshStaysDataForShiftUsers(
          shifts.map((shift) => shift.userId as string),
        );
      }
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async handleMultiShiftStandardViewDeleteOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      await firstValueFrom(
        forkJoin(
          shifts.map((shift) => this.shiftsService.deleteShift(shift.id)),
        ),
      );
      shifts.forEach((shift) => {
        this.deleteShiftCommonOperationsService.handleStandardViewDeleteOperation(
          shift,
        );
      });
      this.refreshStaysDataForShiftUsers(
        shifts.map((shift) => shift.userId as string),
      );
      this.timelineCommonOperationsService.refreshTotalProductivityAllFilteredUsers();
      this.timelineCommonOperationsService.refreshKpisForVisibleUsers();
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async handleMultiShiftCellViewDeleteOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      await firstValueFrom(
        forkJoin(
          shifts.map((shift) => this.shiftsService.deleteShift(shift.id)),
        ),
      );
      shifts.forEach((shift) => {
        this.deleteShiftCommonOperationsService.handleCellViewDeleteOperation(
          shift,
        );
      });
      this.timelineCommonOperationsService.refreshTotalProductivityAllFilteredUsers();
      this.timelineCommonOperationsService.refreshKpisForVisibleUsers();
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  public refreshStaysDataForShiftUsers(userIds: string[]): void {
    this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
      userIds,
    );
  }
}
